import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel, Badge, EmptyState } from "./BuyerPortal";
import BrandCard from "@/components/BrandCard";
import { PAID_PENDING_MESSAGE } from "@/components/PartnerProgramme";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/brand";
import { Lock, GraduationCap, Upload, PhoneCall, MessageCircle } from "lucide-react";

const fmt = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const TONE = { active: "green", pending: "gold", expired: "red" };

export function useMembership() {
    const [m, setM] = useState(null);
    useEffect(() => { api.get("/partner/membership").then((r) => setM(r.data)).catch(() => {}); }, []);
    return m;
}

export const TypeBadge = ({ type }) => (
    <span className={`inline-block px-2.5 py-1 border text-[10px] uppercase tracking-[0.18em] font-bold ${type === "PAID" ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)] text-black" : "bg-green-100 text-green-700 border-green-500/25"}`}>{type === "PAID" ? "Premium Channel Partner" : "Channel Partner"}</span>
);

export function MembershipCard({ m }) {
    if (!m) return null;
    const paid = m.partner_type === "PAID";
    return (
        <Panel title="My Partnership" testid="partner-membership-card">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 text-[13px]">
                <Item l="Partner Type" v={<TypeBadge type={m.partner_type}/>} testid="pm-type"/>
                <Item l="Registration Status" v={<span className="capitalize font-bold">{m.registration_status}</span>} testid="pm-registration"/>
                <Item l="Membership Status" v={paid ? <Badge tone={TONE[m.membership_status] || "gold"}>{m.membership_status}</Badge> : <span className="text-black/50">Not applicable</span>} testid="pm-membership"/>
                <Item l="Membership Start" v={paid ? fmt(m.membership_start) : "—"} testid="pm-start"/>
                <Item l="Valid Until" v={paid ? fmt(m.membership_expiry) : "—"} testid="pm-expiry"/>
            </div>
            {paid && m.membership_status === "pending" && (
                <div className="mt-5 border-l-4 border-[var(--ebg-gold)] bg-[var(--ebg-gold)]/[0.07] p-4" data-testid="pm-pending-banner">
                    <div className="font-bold text-[13px]">Premium Channel Partner Registration – Pending EBG Review</div>
                    <p className="text-[13px] text-black/65 mt-1">{PAID_PENDING_MESSAGE}</p>
                </div>
            )}
            {paid && m.membership_status === "expired" && (
                <div className="mt-5 border-l-4 border-red-500 bg-red-50 p-4 text-[13px]" data-testid="pm-expired-banner">Your Premium membership has expired. Please contact EBG Realty to renew.</div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="pm-benefits">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50 mr-1">Available benefits</span>
                {m.benefits.map((b) => <span key={b} className="border border-black/12 px-2.5 py-1 text-[12px]">{b}</span>)}
            </div>
            <Link to="/portal/partner/listings" data-testid="pm-upload-cta" className="mt-5 inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black"><Upload size={13}/> Upload / Manage Properties</Link>
        </Panel>
    );
}

function Item({ l, v, testid }) {
    return <div data-testid={testid}><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/45 mb-1.5">{l}</div>{v}</div>;
}

export function Locked({ title, testid }) {
    return (
        <Panel title={title} testid={testid}>
            <div className="py-10 text-center" data-testid={`${testid}-locked`}>
                <Lock className="mx-auto text-[var(--ebg-gold)]" size={26}/>
                <div className="font-serif text-2xl mt-3">Available to Active Premium Channel Partners</div>
                <p className="text-black/60 text-[13px] mt-2 max-w-md mx-auto">This section is available once EBG Realty confirms and activates a Premium Channel Partner membership.</p>
            </div>
        </Panel>
    );
}

function usePaidResource(path) {
    const [state, setState] = useState({ loading: true });
    useEffect(() => {
        api.get(path).then((r) => setState({ data: r.data })).catch((e) => setState({ locked: e?.response?.status === 403, error: true }));
    }, [path]);
    return state;
}

export function PartnerTraining() {
    const s = usePaidResource("/partner/paid/training");
    if (s.loading) return null;
    if (s.error) return <Locked title="Training" testid="partner-training"/>;
    return (
        <Panel title="Training" testid="partner-training">
            <div className="py-10 text-center" data-testid="partner-training-placeholder">
                <GraduationCap className="mx-auto text-[var(--ebg-gold)]" size={28}/>
                <div className="font-serif text-2xl mt-3">Training coming soon</div>
                <p className="text-black/60 text-[13px] mt-2">{s.data.message}</p>
            </div>
        </Panel>
    );
}

export function PartnerBrands() {
    const s = usePaidResource("/partner/paid/brands");
    if (s.loading) return null;
    if (s.error) return <Locked title="EBG Brands" testid="partner-brands"/>;
    const items = s.data.items || [];
    return (
        <Panel title={`EBG Brands (${items.length})`} testid="partner-brands">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 -mt-2">
                <p className="text-[12px] text-black/55">Brand and franchise opportunities you can offer to your clients. Contact the EBG commercial desk to take any opportunity forward.</p>
                <div className="flex gap-2">
                    <a href={CONTACT.tel} data-testid="partner-brands-call" className="border border-black/15 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5 hover:border-black"><PhoneCall size={12}/> Call</a>
                    <a href={waLink("Hi EBG Realty, I'm a Premium Channel Partner and would like to discuss an EBG Brand opportunity.")} target="_blank" rel="noopener noreferrer" data-testid="partner-brands-whatsapp" className="border border-black/15 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5 hover:border-emerald-600"><MessageCircle size={12}/> WhatsApp</a>
                </div>
            </div>
            {items.length === 0 ? <EmptyState msg="No brands published yet."/> : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">{items.map((b) => <BrandCard key={b.id} brand={b}/>)}</div>
            )}
        </Panel>
    );
}
